import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { CardListConfig, RappiderAlertModule, RappiderButtonModule, RappiderListGridModule, RappiderModalModule } from '@rappider/rappider-components';
import { JoyrideModule } from 'ngx-joyride';
import { ProjectChannelMessage } from 'libs/rappider-sdk/src/lib/models';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { ButtonSize, ButtonType } from '@rappider/rappider-components/utils';
import { NzSelectModule } from 'ng-zorro-antd/select';

@Component({
  selector: 'rappider-chat-input',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzInputModule,
    NzButtonModule,
    NzDropDownModule,
    JoyrideModule,
    NzToolTipModule,
    NzIconModule,
    RappiderButtonModule,
    NzModalModule,
    NzSelectModule,
    RappiderListGridModule,
    RappiderModalModule,
    RappiderAlertModule
  ],
  templateUrl: './chat-input.component.html',
  styleUrls: ['./chat-input.component.scss']
})
export class ChatInputComponent implements OnChanges, AfterViewInit {

  @Input() activeFeature: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  @Input() features: any[] = []; // eslint-disable-line @typescript-eslint/no-explicit-any
  @Input() activeEntityItem: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  @Input() entityCardListConfig: CardListConfig | undefined;
  @Input() isAiInProgress?: boolean = false;
  @Input() dropdownColumns? = [];
  @Input() listGridConfig?;
  @Input() buttonSelectionWrapper = false;

  /* Displays previous and next button for message navigation */
  @Input() displayMessageNavigationButtons?: boolean = true;

  /* joyride tour inputs */
  @Input() joyrideStepOne?: { joyrideStep: string; title: string; text: string };
  @Input() joyrideStepTwo?: { joyrideStep: string; title: string; text: string };
  @Input() isTourDone?: string;

  // to navigate and show the previous or next messages
  @Input() currentUserMessage?: ProjectChannelMessage | undefined;
  @Input() previousMessage?: ProjectChannelMessage | undefined;
  @Input() nextMessage?: ProjectChannelMessage | undefined;

  @Output() messageSent = new EventEmitter<{ message: string; files?: File[] }>();
  @Output() featureSelected = new EventEmitter<any>(); // eslint-disable-line @typescript-eslint/no-explicit-any
  @Output() changeEntityButtonClicked = new EventEmitter<string>();
  @Output() isChatInputRendered = new EventEmitter<boolean>();
  @Output() displayDataSourcesButtonClicked = new EventEmitter<string>();

  /* joyride events */
  @Output() nextClick = new EventEmitter<string>();
  @Output() doneClick = new EventEmitter();

  /* message navigation */
  @Output() goToPreviousMessages = new EventEmitter<string>();
  @Output() goToNextMessages = new EventEmitter<string>();

  /* file upload */
  @Output() fileUploadButtonClicked = new EventEmitter<void>();
  @Output() filteredDataSources = new EventEmitter<void>();

  @ViewChild('textArea') textArea!: ElementRef;
  @ViewChild('fileInputRef') fileInputRef!: ElementRef<HTMLInputElement>;

  message = '';
  textToWrite = 'Can you give me information about the data?';
  displayAIFeaturesDropdown = false;
  visibleFeatures;
  primaryFeature = { name: 'RAGSearch', title: 'RAG Search', description: 'RAG Search', id: 'ragsearch' };
  dataSourceModalVisibility = false;
  temporarySelectedDataSourceList = null;

  // File upload properties
  uploadedFiles: File[] = [];
  showPreviewModal = false;
  previewImage: string | null = null;

  // Expose enums for template use
  ButtonSize = ButtonSize;
  ButtonType = ButtonType;

  /**
   * Guard to avoid accidental double submit when Enter is pressed rapidly
   */
  private lastEnterAt = 0;
  private readonly enterDebounceMs = 250;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isTourDone) {
      if (this.isTourDone === 'true') {
        this.autoTyping();
      }
    }

    if (changes.dropdownColumns) {
      this.visibleColumns = this.dropdownColumns?.map(column => column?.name);
    }
    if (changes.features || changes.activeFeature) {
      this.visibleFeatures = this.features.filter(feature => (feature.name !== this.activeFeature.name));
    }
  }

  ngAfterViewInit(): void {
    this.isChatInputRendered.emit(true);
  }

  onSendMessage() {
    if ((this.message.trim() || this.uploadedFiles.length > 0) && !this.isAiInProgress) {
      this.messageSent.emit({
        message: this.message,
        files: this.uploadedFiles.length > 0 ? this.uploadedFiles : undefined
      });
      this.message = '';
      this.uploadedFiles = [];
    }
  }

  onDataSourcesButtonClicked() {
    this.displayDataSourcesButtonClicked.emit(new Date().toISOString());
  }

  onKeyPress(event: KeyboardEvent) {
    if ((event.key === 'Enter') && (!(event.shiftKey || event.metaKey))) {
      event.preventDefault();
      const now = Date.now();
      if (now - this.lastEnterAt > this.enterDebounceMs) {
        this.lastEnterAt = now;
        this.onSendMessage();
      }
    }
  }

  onFeatureSelected(feature: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
    this.featureSelected.emit(feature);
    this.activeFeature = feature;
  }

  onHelpButtonClicked() {
  }

  onActiveAgentNameClicked() {
    if (this.features.length > 0) {
      // this.displayAIFeaturesDropdown = true;
    }
  }

  onInfoButtonClicked() {
  }

  onChangeEntityButtonClicked() {
    this.changeEntityButtonClicked.emit(new Date().toISOString());
  }


  autoTyping() {
    let i = 0;
    const interval = setInterval(() => {
      if (i < this.textToWrite.length) {
        this.textArea.nativeElement.value += this.textToWrite.charAt(i);
        i++;
      } else {
        clearInterval(interval);
        this.autoEnterKeyPress();
      }
    }, 100);
  }

  autoEnterKeyPress() {
    this.message = this.textToWrite;
    this.onSendMessage();
    const enterEvent = new KeyboardEvent('keydown', { key: 'Enter' });
    this.textArea.nativeElement.dispatchEvent(enterEvent);
    this.textArea.nativeElement.value = '';
  }

  onNextClick(step: string) {
    this.nextClick.emit(step);
  }

  onJoyrideDoneButtonClick() {
    this.doneClick.emit();
  }

  onGoToPreviousMessages(messageId: string) {
    if (messageId) {
      this.goToPreviousMessages.emit(messageId);
    }
  }

  onGoToNextMessages(messageId: string) {
    if (messageId) {
      this.goToNextMessages.emit(messageId);
    }
  }

  onFileUploadButtonClicked() {
    this.fileInputRef.nativeElement.click();
    this.fileUploadButtonClicked.emit();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.uploadedFiles = Array.from(input.files);
    }
  }

  removeFile(index: number) {
    this.uploadedFiles.splice(index, 1);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileIcon(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return 'file-pdf';
      case 'doc':
      case 'docx': return 'file-word';
      case 'xls':
      case 'xlsx': return 'file-excel';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return 'file-image';
      case 'txt':
      case 'md': return 'file-text';
      case 'json':
      case 'xml': return 'file-code';
      default: return 'file';
    }
  }

  public isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  public getFilePreviewUrl(file: File): string {
    return URL.createObjectURL(file);
  }

  public handlePreview(file: File): void {
    if (this.isImageFile(file)) {
      this.previewImage = this.getFilePreviewUrl(file);
      this.showPreviewModal = true;
    } else {
      // For non-image files, download
      const url = URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  }

  public handleFileClick(file: File): void {
    this.handlePreview(file);
  }

  public handleRemoveFile(index: number): void {
    this.removeFile(index);
  }

  public closePreviewModal(): void {
    this.showPreviewModal = false;
    this.previewImage = null;
  }
  visibleColumns;

  onDropdownColumnChange() {
    this.filteredDataSources.emit(this.visibleColumns);
  }

  onDataSourceSelectModalOpen() {
    this.dataSourceModalVisibility = true;
  }

  dataSourceModalCancelClick() {
    this.dataSourceModalVisibility = false;
  }

  dataSourceModalOkClick() {
    this.filteredDataSources.emit(this.temporarySelectedDataSourceList || this.dropdownColumns);
    this.dataSourceModalVisibility = false;
  }

  onSelectionChange(selectedValues) {
    this.temporarySelectedDataSourceList = selectedValues;
  }
}
